import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { servicesContent } from '../data/servicesContent';

/**
 * THE SOULBOARD METHOD
 * Left: kicker, headline, subhead, intro, note. Right: four expandable steps.
 */

const MethodAccordion = ({ steps }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="border-t border-neutral-300">
      {steps.map((p, i) => (
        <div key={p.n} className="border-b border-neutral-300">
          <button
            type="button"
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center justify-between gap-3 py-4 text-left"
            data-testid={`method-step-${i}`}
          >
            <span className="flex items-start gap-4 flex-1">
              <span>
                <span className="block text-base lg:text-lg font-bold text-neutral-900 leading-tight">{p.title}</span>
                {p.meta && (
                  <span className="block text-[10px] tracking-[0.25em] uppercase mt-1" style={{ color: '#7d65b8' }}>
                    {p.meta}
                  </span>
                )}
              </span>
            </span>
            <ChevronDown
              size={18}
              className={`flex-shrink-0 text-neutral-500 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`}
            />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pb-5 pl-10 space-y-3">
                  {p.body.map((para, bi) => (
                    <p key={bi} className="text-sm lg:text-base text-neutral-700 leading-relaxed">
                      {para}
                    </p>
                  ))}
                  {p.includes && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pt-1">
                      {p.includes.map((it) => (
                        <li key={it} className="text-sm lg:text-base text-neutral-800 flex items-start gap-2">
                          <span style={{ color: '#CCFF00' }} className="mt-0.5 font-bold">·</span>
                          {it}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const WhatIsSoulboard = () => {
  const { language } = useLanguage();
  const c = servicesContent[language];

  return (
    <section id="soulboard-method" className="py-24 lg:py-28 bg-[#faf7ee]/40 overflow-hidden" data-testid="soulboard-method-section">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* LEFT: intro */}
            <motion.div
              className="lg:col-span-6 space-y-5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[11px] tracking-[0.4em] uppercase text-neutral-500">{c.methodEyebrow}</p>
              <h2
                className="offering-title leading-[0.95] text-neutral-900"
                style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2.25rem, 4.5vw, 3.75rem)' }}
              >
                {c.methodTitle}
              </h2>
              <p className="text-[11px] tracking-[0.35em] uppercase" style={{ color: '#7d65b8' }}>
                {c.methodSubhead}
              </p>
              <div className="w-12 h-[2px] bg-[#CCFF00]" />

              {c.methodMeta && (
                <div className="flex flex-wrap gap-x-10 gap-y-2" data-testid="soulboard-method-meta">
                  {c.methodMeta.map((m) => (
                    <div key={m.label}>
                      <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: '#7d65b8' }}>
                        {m.label}
                      </p>
                      <p className="text-sm lg:text-base text-neutral-900 font-medium">{m.value}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3 text-sm lg:text-base text-neutral-700 leading-relaxed">
                {c.methodIntro.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <p className="text-xs lg:text-sm text-neutral-500 leading-relaxed">{c.methodNote}</p>
            </motion.div>

            {/* RIGHT: editorial portrait, natural aspect */}
            <motion.div
              className="lg:col-span-6"
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="/melinda-foryou-barcelona.jpg"
                alt="The Soulboard Method"
                className="block w-full h-auto"
                loading="lazy"
                data-testid="method-image"
              />
            </motion.div>
          </div>

          {/* THE PROCESS — full width, light editorial accordion */}
          <motion.div
            className="mt-14 lg:mt-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[11px] tracking-[0.4em] uppercase text-neutral-500 mb-4">
              {language === 'hu' ? 'A FOLYAMAT' : 'THE PROCESS'}
            </p>
            <MethodAccordion steps={c.methodSteps} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhatIsSoulboard;
